import {DependencyList, RefCallback, useCallback, useEffect, useRef, useState} from 'react';

import useResizeObserver from 'ui-library/hooks/useResizeObserver';

export interface UseIsTruncatedResult<T extends HTMLElement> {
  /**
   * Attach to the single-line text element that truncates. It must have `overflow: hidden`,
   * `text-overflow: ellipsis`, and `white-space: nowrap` for the measurement to be meaningful.
   */
  ref: RefCallback<T>;
  /** True while the element is clipping its content (`scrollWidth > clientWidth`). */
  isTruncated: boolean;
}

/**
 * Tracks whether an ellipsis-truncating element is actually clipping its content, so callers can reveal
 * the full value (e.g. in a Tooltip) only when it no longer fits. Re-checks on element resize and
 * whenever `deps` change — pass the rendered content so a text change re-measures, since a content-only
 * change leaves the element's box size (and thus the resize observer) untouched.
 */
export default function useIsTruncated<T extends HTMLElement>(
  deps: DependencyList = []
): UseIsTruncatedResult<T> {
  const [isTruncated, setIsTruncated] = useState(false);
  const elementRef = useRef<T | null>(null);

  const checkTruncation = useCallback((): void => {
    const el = elementRef.current;
    setIsTruncated(el != null && el.scrollWidth > el.clientWidth);
  }, []);

  const {ref: resizeRef} = useResizeObserver<T>({onResize: checkTruncation});
  const ref = useCallback<RefCallback<T>>(
    (node) => {
      resizeRef(node);
      elementRef.current = node;
    },
    [resizeRef]
  );

  useEffect(() => {
    checkTruncation();
    // `deps` lets callers re-measure on content change; `checkTruncation` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkTruncation, ...deps]);

  return {ref, isTruncated};
}
